import { PersistentRoom } from '../../types/room';
import { AvatarGroup } from '../common/Avatar';

interface RoomCardProps {
  room: PersistentRoom;
  onlineCount?: number;
  onlineNames?: string[];
  memberAvatars?: { name: string; imageUrl?: string }[];
  onEnter: (room: PersistentRoom) => void;
  onSettings?: (room: PersistentRoom) => void;
}

/**
 * Card component displaying a persistent room.
 */
export function RoomCard({
  room,
  onlineCount = 0,
  onlineNames = [],
  memberAvatars = [],
  onEnter,
  onSettings,
}: RoomCardProps) {
  return (
    <div className="card card-interactive group flex flex-col overflow-hidden">
      <div
        className="relative aspect-[16/10] overflow-hidden cursor-pointer"
        onClick={() => onEnter(room)}
      >
        {room.imageUrl ? (
          <img
            src={room.imageUrl}
            alt={room.name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="room-image-placeholder w-full h-full">
            <svg
              className="w-12 h-12"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          </div>
        )}

        {onlineCount > 0 && (
          <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 bg-black/70 backdrop-blur-sm rounded-full">
            <span className="online-dot" />
            <span className="text-xs font-medium">{onlineCount}</span>
          </div>
        )}
      </div>

      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3
            className="font-semibold text-white truncate cursor-pointer hover:text-gray-300 transition-colors"
            onClick={() => onEnter(room)}
          >
            {room.name}
          </h3>

          {onSettings && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSettings(room);
              }}
              className="p-1.5 -m-1.5 text-gray-500 hover:text-white hover:bg-white/5 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
              title="Room settings"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                />
              </svg>
            </button>
          )}
        </div>

        <div className="mt-auto space-y-2">
          {memberAvatars.length > 0 && (
            <AvatarGroup items={memberAvatars} size="sm" />
          )}
          {onlineCount > 0 ? (
            <p className="text-sm text-gray-400 truncate">
              {onlineNames.length > 0
                ? onlineNames.slice(0, 3).join(', ') +
                  (onlineNames.length > 3
                    ? ` +${onlineNames.length - 3} more`
                    : '')
                : `${onlineCount} in room`}
            </p>
          ) : (
            <p className="text-sm text-gray-500">Empty</p>
          )}
        </div>

        <button
          onClick={() => onEnter(room)}
          className="btn btn-primary w-full mt-4"
        >
          Enter
        </button>
      </div>
    </div>
  );
}
